/**
 * ChatHeader (Doc 58) — slim header showing conversation title + Export button.
 *
 * Sits trên ChatStream, bên dưới TopBar/breadcrumbs. Chỉ render khi có
 * active conversation.
 */
import { Download, Loader2 } from "lucide-react";
import * as React from "react";

import { conversationsApi } from "@/api/conversations";
import { Button } from "@/components/ui/button";
import { useChatStore } from "@/stores/chat";
import { useUIStore } from "@/stores/ui";

export function ChatHeader(): JSX.Element | null {
  const activeId = useChatStore((s) => s.activeConversationId);
  const conversations = useChatStore((s) => s.conversations);
  const pushToast = useUIStore((s) => s.pushToast);
  const [exporting, setExporting] = React.useState(false);

  const conv = conversations.find((c) => c.id === activeId);
  if (!activeId) return null;

  const handleExport = async (): Promise<void> => {
    setExporting(true);
    try {
      const { blob, filename } = await conversationsApi.export(activeId, "markdown");
      // Trigger browser download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      pushToast({
        variant: "success",
        title: "Đã export conversation",
        description: filename,
      });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Export thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="border-b px-4 py-1.5 flex items-center gap-2 shrink-0 bg-background/60">
      <div className="flex-1 min-w-0 text-sm font-medium truncate">
        {conv?.title ?? "Hội thoại"}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void handleExport()}
        disabled={exporting}
        title="Export conversation thành markdown"
      >
        {exporting ? (
          <Loader2 className="size-3.5 animate-spin mr-1" />
        ) : (
          <Download className="size-3.5 mr-1" />
        )}
        Export
      </Button>
    </div>
  );
}
